import { useMutation, useQueryClient } from '@tanstack/react-query';
import { parseSummaryDocument } from '@libertify/ui';
import { assetUrl, writeJson } from '../addOnAssets';
import { keyPointsKey } from './helpers';
import type { KeyPointsContent, KeyPointsSaver, UseKeyPointsArgs } from './types';
import type { AddOnType, AssetPatchBody } from '../../types';

/**
 * The endpoint overwrites the description with whatever the request carries —
 * an absent one included — so a save sends back the one it read. This is only
 * the fallback for a save with no read cached: the generator names the asset
 * after its add-on type.
 */
const FALLBACK_DESCRIPTION: AddOnType = 'Summary';

/**
 * Writes a project's Key points back to its asset:
 * `PATCH /media/assets/{assetId}` with `{ add_on: { content } }`.
 *
 * `{ content }` is the only shape of the untagged `add_on` union that
 * resolves to `ExecutiveSummary`; anything else is rejected as "invalid data
 * type".
 *
 * Caches like `useSaveQuiz`: the summary is written into the query cache when
 * the save STARTS and is not invalidated, so a card reopened mid-flight seeds
 * from the edit rather than from the pre-edit document. On failure the cache
 * goes back to what it held before the write.
 */
export function useSaveKeyPoints({
  apiBaseUrl,
  projectId,
  token,
}: UseKeyPointsArgs): KeyPointsSaver {
  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationFn: ({
      assetId,
      content,
      description,
    }: {
      assetId: string;
      content: string;
      description: string;
    }) =>
      writeJson(
        `${assetUrl(apiBaseUrl, assetId)}?description=${encodeURIComponent(description)}`,
        { add_on: { content } } satisfies AssetPatchBody,
        token,
      ),
    onMutate: async ({ assetId, content, description }) => {
      const queryKey = keyPointsKey(apiBaseUrl, projectId);
      // A read still in flight would land after this write and put the
      // pre-edit summary back.
      await queryClient.cancelQueries({ queryKey });
      const previous = queryClient.getQueryData<KeyPointsContent>(queryKey);
      queryClient.setQueryData<KeyPointsContent>(queryKey, {
        content,
        document: parseSummaryDocument(content),
        assetId,
        description,
      });
      return { previous };
    },
    onError: (_error, _variables, context) => {
      const queryKey = keyPointsKey(apiBaseUrl, projectId);
      // setQueryData ignores `undefined`, so an empty cache is emptied again
      // rather than left holding the rejected summary.
      if (context?.previous) {
        queryClient.setQueryData<KeyPointsContent>(queryKey, context.previous);
      } else {
        queryClient.removeQueries({ queryKey });
      }
    },
  });

  return {
    save: (assetId, content) => {
      const cached = queryClient.getQueryData<KeyPointsContent>(
        keyPointsKey(apiBaseUrl, projectId),
      );
      const description =
        cached?.assetId === assetId && cached.description
          ? cached.description
          : FALLBACK_DESCRIPTION;
      return mutation.mutateAsync({ assetId, content, description });
    },
    isSaving: mutation.isLoading,
  };
}
